import { Component, OnDestroy, OnInit } from '@angular/core';
import { TranslocoModule } from '@jsverse/transloco';

interface User {
  fullName: string;
  title: string;
  position: string;
  portraitImage: string;
}

interface UserFeedback {
  user: User;
  logoImage: string;
  text1: string;
  text2: string;
  headline: string;
  buildingImage: string;
}

@Component({
  selector: 'app-user-feedback',
  standalone: true,
  imports: [TranslocoModule],
  templateUrl: './user-feedback.component.html',
  styleUrls: ['./user-feedback.component.scss']
})
export class UserFeedbackComponent implements OnInit, OnDestroy {
  activeSlideIndex = 0;
  announcesSlideChanges = false;
  private autoplayIntervalId: ReturnType<typeof setInterval> | null = null;
  private readonly autoplayDelayMs = 6000;
  private readonly swipeThresholdPx = 40;
  private touchStartX: number | null = null;
  private prefersReducedMotion = false;
  private readonly onVisibilityChange = (): void => {
    if (document.hidden) {
      this.stopAutoplay();
      return;
    }

    this.startAutoplay();
  };

  readonly userFeedbacks: UserFeedback[] = [
    {
      user: {
        fullName: 'products.userfeedback.3.user',
        title: 'products.userfeedback.3.title',
        position: 'products.userfeedback.3.position',
        portraitImage: '/assets/images/user-feedback/3/heinrich_degelo.avif',
      },
      logoImage: '/assets/images/user-feedback/3/dg_logo.avif',
      text1: 'products.userfeedback.3.text1',
      text2: 'products.userfeedback.3.text2',
      headline: 'products.userfeedback.3.headline',
      buildingImage: '/assets/images/user-feedback/3/hd_building.avif',
    },
    {
      user: {
        fullName: 'products.userfeedback.4.user',
        title: 'products.userfeedback.4.title',
        position: 'products.userfeedback.4.position',
        portraitImage: '/assets/images/user-feedback/4/patrik_gartmann.avif',
      },
      logoImage: '/assets/images/user-feedback/4/fg_logo.avif',
      text1: 'products.userfeedback.4.text1',
      text2: 'products.userfeedback.4.text2',
      headline: 'products.userfeedback.4.headline',
      buildingImage: '/assets/images/user-feedback/4/pg_building.avif',
    },
    {
      user: {
        fullName: 'products.userfeedback.5.user',
        title: 'products.userfeedback.5.title',
        position: 'products.userfeedback.5.position',
        portraitImage: '/assets/images/user-feedback/5/florian_bischoff.avif',
      },
      logoImage: '/assets/images/user-feedback/5/fb_logo.avif',
      text1: 'products.userfeedback.5.text1',
      text2: 'products.userfeedback.5.text2',
      headline: 'products.userfeedback.5.headline',
      buildingImage: '/assets/images/user-feedback/5/fb_building.avif',
    },
    {
      user: {
        fullName: 'products.userfeedback.user',
        title: 'products.userfeedback.title',
        position: 'products.userfeedback.position',
        portraitImage: '/assets/images/user-feedback/rb.jpg',
      },
      logoImage: '/assets/images/user-feedback/logo.jpg',
      text1: 'products.userfeedback.text1',
      text2: 'products.userfeedback.text2',
      headline: 'products.userfeedback.headline',
      buildingImage: '/assets/images/user-feedback/gebaeude.jpg',
    },
    {
      user: {
        fullName: 'products.userfeedback.2.user',
        title: 'products.userfeedback.2.title',
        position: 'products.userfeedback.2.position',
        portraitImage: '/assets/images/user-feedback/2/am.jpg',
      },
      logoImage: '/assets/images/user-feedback/2/logo.png',
      text1: 'products.userfeedback.2.text1',
      text2: 'products.userfeedback.2.text2',
      headline: 'products.userfeedback.2.headline',
      buildingImage: '/assets/images/user-feedback/2/gebaeude-small.jpg',
    },
  ];

  ngOnInit(): void {
    this.prefersReducedMotion =
      typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    this.startAutoplay();
  }

  ngOnDestroy(): void {
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    this.stopAutoplay();
  }

  goToSlide(index: number): void {
    this.activeSlideIndex = index;
    this.restartAutoplay();
    this.announcesSlideChanges = true;
  }

  prevSlide(): void {
    this.activeSlideIndex =
      this.activeSlideIndex === 0 ? this.userFeedbacks.length - 1 : this.activeSlideIndex - 1;
    this.restartAutoplay();
    this.announcesSlideChanges = true;
  }

  nextSlide(): void {
    this.activeSlideIndex =
      this.activeSlideIndex === this.userFeedbacks.length - 1 ? 0 : this.activeSlideIndex + 1;
    this.announcesSlideChanges = true;
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.prevSlide();
      return;
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.nextSlide();
    }
  }

  onPointerEnter(): void {
    this.stopAutoplay();
  }

  onPointerLeave(): void {
    this.startAutoplay();
  }

  onTouchStart(event: TouchEvent): void {
    this.touchStartX = event.changedTouches[0]?.clientX ?? null;
  }

  onTouchEnd(event: TouchEvent): void {
    const touchEndX = event.changedTouches[0]?.clientX;
    if (this.touchStartX === null || touchEndX === undefined) {
      this.touchStartX = null;
      return;
    }

    const deltaX = touchEndX - this.touchStartX;
    if (Math.abs(deltaX) < this.swipeThresholdPx) {
      this.touchStartX = null;
      return;
    }

    if (deltaX > 0) {
      this.prevSlide();
    } else {
      this.nextSlide();
    }

    this.touchStartX = null;
  }

  private startAutoplay(): void {
    if (this.prefersReducedMotion || this.userFeedbacks.length < 2 || this.autoplayIntervalId !== null) {
      return;
    }

    this.autoplayIntervalId = setInterval(() => {
      this.nextSlide();
    }, this.autoplayDelayMs);
  }

  private stopAutoplay(): void {
    if (this.autoplayIntervalId !== null) {
      clearInterval(this.autoplayIntervalId);
      this.autoplayIntervalId = null;
    }
  }

  private restartAutoplay(): void {
    this.stopAutoplay();
    this.startAutoplay();
  }
}
